import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';
import Widget from '../../../components/Widget';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import moment from 'moment';
import { Row, Col, Statistic } from 'antd';

am4core.useTheme(am4themes_animated);

function FuelConsumptionHours(props) {
    const [header, setHeader] = useState('--');
    const [chartData, setChartData] = useState([]);
    const [averages, setAverages] = useState({
        ignition: 0,
        moving: 0,
        idle: 0,
        rateIgnition: 0
    });
    const chart = useRef(null);

    useEffect(() => {
        if (props.selectedGroup && props.assetData) {
            const title = props.selectedGroup === 'w' ? 'Weekly' : props.selectedGroup === 'm' ? 'Monthwise' : props.selectedGroup === 'y' ? 'Yearwise' : 'Daywise';
            setHeader(`${title} Ignition, Moving & Idle Time with Consumption Rate for ${props.assetData.assetCode}`);
        }
    }, [props.selectedGroup, props.assetData]);


    useEffect(() => {
        if (props.data && props.data.length) {
            const transformedData = props.data.map(item => {
                const ignitionHours = item.ignition_time_ms ? item.ignition_time_ms / (1000 * 60 * 60) : 0;
                const idleHours = item.idle_time_ms ? item.idle_time_ms / (1000 * 60 * 60) : 0;
                const movingHours = item.moving_time_ms ? item.moving_time_ms / (1000 * 60 * 60) : 0;
                const fuelConsumed = item.fuel_consumed || 0;
                const consumptionRateIgnition = ignitionHours > 0 ? fuelConsumed / ignitionHours : 0;
                return {
                    date: item.date,
                    ignition_time: ignitionHours,
                    idle_time: idleHours,
                    moving_time: movingHours,
                    consumption_rate_ignition: consumptionRateIgnition
                };
            });
            setChartData(transformedData);

            // Calculate averages
            const totalDays = transformedData.length;
            const totalIgnition = transformedData.reduce((sum, item) => sum + item.ignition_time, 0);
            const totalMoving = transformedData.reduce((sum, item) => sum + item.moving_time, 0);
            const totalIdle = transformedData.reduce((sum, item) => sum + item.idle_time, 0);
            const totalRateIgnition = transformedData.reduce((sum, item) => sum + item.consumption_rate_ignition, 0);

            setAverages({
                ignition: totalIgnition / totalDays,
                moving: totalMoving / totalDays,
                idle: totalIdle / totalDays,
                rateIgnition: totalRateIgnition / totalDays
            });
        }
    }, [props.data]);

    useLayoutEffect(() => {
        if (chartData && chartData.length) {
            let x = am4core.create('timeDistributionChart', am4charts.XYChart);
            x.hiddenState.properties.opacity = 0;

            let title = x.titles.create();
            title.text = header || '';
            title.fontSize = 15;
            title.marginBottom = 18;

            // Add Export Menu
            x.exporting.menu = new am4core.ExportMenu();
            x.exporting.filePrefix = 'fuel_consumption_hours_data' + new Date().getTime();

            x.paddingRight = 20;

            // X Axis (date)
            let categoryAxis = x.xAxes.push(new am4charts.CategoryAxis());
            categoryAxis.dataFields.category = 'date';
            categoryAxis.renderer.grid.template.location = 0;
            categoryAxis.renderer.minGridDistance = 30;
            categoryAxis.renderer.labels.template.rotation = 90;
            categoryAxis.renderer.labels.template.horizontalCenter = 'middle';
            categoryAxis.renderer.labels.template.verticalCenter = 'middle';
            // Add gap between each record
            categoryAxis.renderer.cellStartLocation = 0.2;
            categoryAxis.renderer.cellEndLocation = 0.8;

            // Y Axis (hours)
            let valueAxis = x.yAxes.push(new am4charts.ValueAxis());
            valueAxis.title.text = 'Hours';
            valueAxis.min = 0;

            // Right Y Axis (consumption rate)
            let valueAxisRight = x.yAxes.push(new am4charts.ValueAxis());
            valueAxisRight.title.text = 'Consumption Rate (liters/hour)';
            valueAxisRight.min = 0;
            valueAxisRight.renderer.opposite = true;
            valueAxisRight.syncWithAxis = valueAxis;

            // Ignition Time Series (grouped bar)
            let ignitionSeries = x.series.push(new am4charts.ColumnSeries());
            ignitionSeries.name = 'Ignition Time';
            ignitionSeries.dataFields.valueY = 'ignition_time';
            ignitionSeries.dataFields.categoryX = 'date';
            ignitionSeries.columns.template.tooltipText = 'Ignition Time: {valueY.formatNumber("#.00")} hours';
            ignitionSeries.columns.template.width = am4core.percent(60);
            ignitionSeries.clustered = true;
            ignitionSeries.columns.template.strokeWidth = 0;
            ignitionSeries.columns.template.fill = am4core.color('#5bc0de');
            ignitionSeries.hidden = false;

            // Moving Time Series (grouped bar)
            let movingSeries = x.series.push(new am4charts.ColumnSeries());
            movingSeries.name = 'Moving Time';
            movingSeries.dataFields.valueY = 'moving_time';
            movingSeries.dataFields.categoryX = 'date';
            movingSeries.columns.template.tooltipText = 'Moving Time: {valueY.formatNumber("#.00")} hours';
            movingSeries.columns.template.fill = am4core.color('#6bc352');
            movingSeries.columns.template.width = am4core.percent(60);
            movingSeries.clustered = true;
            movingSeries.columns.template.strokeWidth = 0;
            movingSeries.hidden = true;

            // Idle Time Series (grouped bar)
            let idleSeries = x.series.push(new am4charts.ColumnSeries());
            idleSeries.name = 'Idle Time';
            idleSeries.dataFields.valueY = 'idle_time';
            idleSeries.dataFields.categoryX = 'date';
            idleSeries.columns.template.tooltipText = 'Idle Time: {valueY.formatNumber("#.00")} hours';
            idleSeries.columns.template.fill = am4core.color('#fcc034');
            idleSeries.columns.template.width = am4core.percent(60);
            idleSeries.clustered = true;
            idleSeries.columns.template.strokeWidth = 0;
            idleSeries.hidden = true;

            // Consumption Rate (Ignition) Line Series
            let consumptionIgnitionSeries = x.series.push(new am4charts.LineSeries());
            consumptionIgnitionSeries.name = 'Consumption Rate (Ignition)';
            consumptionIgnitionSeries.dataFields.valueY = 'consumption_rate_ignition';
            consumptionIgnitionSeries.dataFields.categoryX = 'date';
            consumptionIgnitionSeries.yAxis = valueAxisRight;
            consumptionIgnitionSeries.stroke = am4core.color('#722ed1');
            consumptionIgnitionSeries.strokeWidth = 2;
            consumptionIgnitionSeries.tooltipText = 'Consumption Rate (Ignition): {valueY.formatNumber("#.00")} L/h';
            consumptionIgnitionSeries.bullets.push(new am4charts.CircleBullet());
            consumptionIgnitionSeries.bullets.getIndex(0).circle.fill = am4core.color('#722ed1');
            consumptionIgnitionSeries.bullets.getIndex(0).circle.strokeWidth = 2;
            consumptionIgnitionSeries.bullets.getIndex(0).circle.radius = 4;
            consumptionIgnitionSeries.hidden = false;

            // Add legend
            x.legend = new am4charts.Legend();
            x.legend.position = 'top';
            x.legend.paddingBottom = 20;

            // Add cursor
            x.cursor = new am4charts.XYCursor();
            x.cursor.behavior = 'zoomX';

            x.data = chartData;
            chart.current = x;

            return () => {
                if (x) x.dispose();
            };
        }
    }, [chartData, header]);

    return (
        <Widget>
            <div style={{ padding: '20px' }}>
                {chartData.length > 0 ? (
                    <>
                        <div id="timeDistributionChart" style={{ width: '100%', height: '500px' }} />
                        <Row gutter={16} style={{ marginTop: '20px' }}>
                            <Col span={6}>
                                <Statistic
                                    title="Average Ignition Time"
                                    value={averages.ignition}
                                    precision={2}
                                    suffix="hours"
                                />
                            </Col>
                            <Col span={6}>
                                <Statistic
                                    title="Average Moving Time"
                                    value={averages.moving}
                                    precision={2}
                                    suffix="hours"
                                />
                            </Col>
                            <Col span={6}>
                                <Statistic
                                    title="Average Idle Time"
                                    value={averages.idle}
                                    precision={2}
                                    suffix="hours"
                                />
                            </Col>
                            <Col span={6}>
                                <Statistic
                                    title="Average Ignition Rate"
                                    value={averages.rateIgnition}
                                    precision={2}
                                    suffix="L/h"
                                />
                            </Col>
                        </Row>
                    </>
                ) : (
                    <div>No data available</div>
                )}
            </div>
        </Widget>
    );
}

export default FuelConsumptionHours;
